import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable} from "rxjs";
import {environment} from "../../environments/environment";
import {Session} from "../interfaces/session-data";
import {CardSchema} from "../interfaces/card/CardSchema";

@Injectable({
  providedIn: 'root'
})
export class GooglePayService {

  private paymentDataSubject = new BehaviorSubject<google.payments.api.PaymentData | null>(null);
  private paymentRequest: google.payments.api.PaymentDataRequest;
  private sessionData: Session;
  availableCardTypes = ["AMEX", "DISCOVER", "JCB", "MASTERCARD", "VISA"];
  filteredCardTypes: string[];
  constructor() {
    this.sessionData = JSON.parse(sessionStorage.getItem('sessionData'));
    this.buildPaymentRequest();
  }

  private buildPaymentRequest() {
    this.filteredCardTypes = this.getSupportedCardTypes();
    this.paymentRequest = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
            //@ts-ignore
            allowedCardNetworks: this.filteredCardTypes,
            billingAddressRequired: true,
            billingAddressParameters: {
              phoneNumberRequired: true,
              format: "MIN"
            }
          },
          tokenizationSpecification: {
            type: 'PAYMENT_GATEWAY',
            parameters: {
              gateway: 'adumoonline',
              gatewayMerchantId: 'adumoonline'
            }
          }
        }
      ],
      merchantInfo: {
        merchantId: environment.googlePayMerchantId,
        merchantName: environment.googlePayMerchantName
      },
      transactionInfo: {
        totalPriceStatus: 'FINAL',
        totalPriceLabel: 'Total',
        totalPrice: this.sessionData?.sessionDisplayInformation?.amount.toString(10),
        currencyCode: this.sessionData?.sessionDisplayInformation?.currencyCode,
        countryCode: this.sessionData?.sessionDisplayInformation?.currencyCode.toUpperCase() === 'NAD' ? 'NA' : 'ZA',
      },
      emailRequired: true,
      shippingAddressRequired: false,
      //@ts-ignore
      shippingAddressParameters: {
        phoneNumberRequired: true,
        // allowedCountryCodes: null
      },
    };
  }
  updateTransactionInfo(transactionInfo: Partial<google.payments.api.TransactionInfo>) {
    this.paymentRequest.transactionInfo = {
      ...this.paymentRequest.transactionInfo,
      ...transactionInfo
    };
  }

  initializeGooglePay(): Promise<google.payments.api.PaymentsClient> {
    // this.buildPaymentRequest();
    return new Promise((resolve, reject) => {
      this.loadGooglePayScript().then(() => {
        //@ts-ignore
        const client = new google.payments.api.PaymentsClient({environment: environment.googlePayEnvironment.toString()});
        client.isReadyToPay(this.paymentRequest)
          .then((response) => {
            if (response.result) {
              resolve(client);
            } else {
              reject('Google Pay is not available');
            }
          })
          .catch((error) => reject(error));
      });
    });
  }

  isGooglePayAvailable(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.loadGooglePayScript()
        .then(() => {
          const client = new google.payments.api.PaymentsClient({ environment: "TEST" });
          return client.isReadyToPay(this.paymentRequest);
        })
        .then((response) => {
          resolve(response.result);
        })
        .catch((error) => {
          console.error('Error checking Google Pay availability:', error);
          resolve(false);
        });
    });
  }

  loadPaymentData(client: google.payments.api.PaymentsClient): Promise<google.payments.api.PaymentData> {
    return client.loadPaymentData(this.paymentRequest);
  }

  setPaymentData(data: google.payments.api.PaymentData) {
    this.paymentDataSubject.next(data);
  }

  getPaymentData(): Observable<google.payments.api.PaymentData | null> {
    return this.paymentDataSubject.asObservable();
  }

  generateDisplayItems(): { label: string; amount: { currency: string; value: string } }[] {
    return this.sessionData.orderItems.map(item => ({
      label: `${item.itemName} (x ${item.quantity})`,
      amount: { currency: this.sessionData.sessionDisplayInformation.currencyCode, value: item.amount }
    }));
  }

  private getSupportedCardTypes(): string[] {
    if (!this.sessionData || !this.sessionData.virtualConfiguration) {
      return [];
    }
    let enabledCardTypes = this.extractCodes(this.sessionData.virtualConfiguration.supportedCardSchemas);
    //Replace Codes For Diners and Amex as they differ from what is in our Database.
    enabledCardTypes.includes("DINERSCLUB") ? enabledCardTypes.push( "DISCOVER" ) : null;
    enabledCardTypes.includes("AMERICANEXPRESS") ? enabledCardTypes.push( "AMEX" ) : null;
    return enabledCardTypes.filter(code => this.availableCardTypes.includes(code));
  }

  extractCodes(cards: CardSchema[]): string[] {
    return cards.map(card => card.code.toUpperCase());
  }

  private loadGooglePayScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined' && google.payments) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://pay.google.com/gp/p/js/pay.js';
      script.onload = () => resolve();
      script.onerror = (error) => reject(new Error('Failed to load Google Pay script :'+ error));
      document.body.appendChild(script);
    });
  }

}
